import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { CONFIG, isEntryEditable, type Entry, type Fixture, type Round } from '@h2h/shared';
import { relativeDay, shortDay, timeLabel, useNow } from '../helpers';
import { SlatePager } from './SlatePager';
import type { PicksCtaAction } from './types';
import { StakeLadder, StakeLadderPlaceholder, type LadderRungData } from '../../../components/play/cycling-ui';
import { TOTAL } from '../../make-cycling-picks/constants';
import {
  CyclingDuelsHubSection,
  HubLiveBadge
} from './CyclingDuelsHubCard';

/**
 * Cycling Compete hero — mockup "Duels of the day" card on the league hub.
 *
 *   · No entry yet → ghost ladder placeholders + "Call my duels" CTA.
 *   · All six called → ranked stake ladder inline (drag to rank; autosaves on hub),
 *     plus an "Edit my calls" link back into the picks sheet to change a rider.
 *   · Racing / settled → same ladder, read-only, with live or results chrome.
 */
export function CyclingStageCard({
  round,
  entry,
  slateFixtures,
  settledCount,
  selectableRounds,
  selectedRoundId,
  onSelectRound,
  onPredict,
  onReorder
}: {
  round: Round | null | undefined;
  entry: Entry | null | undefined;
  slateFixtures: Fixture[];
  settledCount: number;
  selectableRounds?: Round[];
  selectedRoundId?: string | null;
  onSelectRound?: (roundId: string) => void;
  onPredict: (action: PicksCtaAction) => void;
  onReorder?: (next: LadderRungData[]) => void;
}) {
  const now = useNow(true);
  const hasEntry = !!entry?.duelPicks?.length;
  const duels = useMemo(
    () => [...slateFixtures].sort((a, b) => a.fixtureId.localeCompare(b.fixtureId)),
    [slateFixtures]
  );

  const rungs: LadderRungData[] = useMemo(() => {
    if (!entry?.duelPicks?.length) return [];
    const byId = new Map(duels.map((f) => [f.fixtureId, f]));
    return [...entry.duelPicks]
      .sort((a, b) => b.confidenceWeight - a.confidenceWeight)
      .map((p) => {
        const fixture = byId.get(p.duelId);
        if (!fixture) return null;
        return { duelId: p.duelId, pick: p.pick, fixture };
      })
      .filter(Boolean) as LadderRungData[];
  }, [entry, duels]);

  const weights = useMemo(
    () =>
      rungs.length
        ? [...entry!.duelPicks!]
            .sort((a, b) => b.confidenceWeight - a.confidenceWeight)
            .map((_, i) => CONFIG.CYCLING.CONFIDENCE_WEIGHTS[i] ?? 1)
        : [...CONFIG.CYCLING.CONFIDENCE_WEIGHTS],
    [rungs, entry]
  );

  if (!round) {
    return (
      <CyclingDuelsHubSection
        title="No open stage right now"
        body="The next stage appears here as soon as it's scheduled."
      />
    );
  }

  const upcoming = round.status === 'SCHEDULED';
  const settled = round.status === 'SETTLED';
  const editable = isEntryEditable(round, [], now);
  const racing = !upcoming && !settled && !editable;
  const calledCount = entry?.duelPicks?.length ?? 0;
  const allCalled = calledCount >= TOTAL;
  const lockAt = round.firstKickoffAt;

  const pager =
    selectableRounds && selectableRounds.length > 1 && onSelectRound ? (
      <SlatePager
        rounds={selectableRounds}
        selectedId={selectedRoundId ?? round.roundId}
        isCycling
        now={now}
        onSelect={onSelectRound}
      />
    ) : null;

  const ladderBody = (
    <>
      Drag to order them by confidence. Your surest call rides at{' '}
      <span className="font-extrabold text-accent-ink">6×</span>, your toss-up at{' '}
      <span className="font-extrabold text-ink">1×</span>.
    </>
  );

  // Hub mockup: once all six are called, the ranked ladder IS the hub card.
  const showLadder = allCalled && hasEntry;

  if (showLadder) {
    return (
      <CyclingDuelsHubSection
        pager={pager}
        title={editable ? 'Rank your six calls' : racing ? 'Duels locked' : settled ? 'Your stake ladder' : 'Your stake ladder'}
        body={
          editable
            ? ladderBody
            : racing
              ? 'The stage is rolling — your ladder is locked in.'
              : 'Your ranked calls for this stage.'
        }
        lockAt={editable ? lockAt : null}
        now={now}
        badge={racing ? <HubLiveBadge /> : undefined}
        cta={
          editable
            ? undefined
            : settled
              ? { label: 'See results', onClick: () => onPredict('results'), variant: 'primary' }
              : { label: 'Division board', onClick: () => onPredict('board'), variant: 'dark' }
        }
        ctaNote={editable ? 'Saved as you rank · re-order anytime before the stage rolls out' : undefined}
        secondaryAction={editable ? { label: 'Edit my calls', onClick: () => onPredict('picks') } : undefined}
      >
        {rungs.length > 0 ? (
          <StakeLadder
            rungs={rungs}
            weights={weights}
            disabled={!editable}
            onReorder={editable ? onReorder : undefined}
            stageLive={racing}
            footerVariant="ranked"
            rankedCount={TOTAL}
            total={TOTAL}
            embedded
          />
        ) : (
          <StakeLadderPlaceholder embedded calledCount={calledCount} />
        )}
      </CyclingDuelsHubSection>
    );
  }

  // ── Pre-ladder — prompt card when the stage isn't open for inline picks ─────
  let title: string;
  let body: string;
  let ctaLabel: string;
  let action: PicksCtaAction;
  let ctaVariant: 'primary' | 'dark' | 'disabled' = 'primary';
  let ctaNote: string | undefined;
  let badge: ReactNode;
  const showPlaceholderLadder =
    !allCalled && !settled && (upcoming ? !hasEntry : editable && !allCalled);

  if (settled) {
    title = 'Stage settled';
    body = 'See how your calls landed and what you scored.';
    ctaLabel = 'See results';
    action = 'results';
    badge = undefined;
  } else if (upcoming) {
    title = round.openAt > now ? `Calls open ${relativeDay(round.openAt, now)}` : 'Opening soon';
    body = `Six terrain-matched duels land when ${round.label ?? 'this stage'} opens.`;
    ctaLabel = round.openAt > now ? `Opens ${shortDay(round.openAt)} · ${timeLabel(round.openAt)}` : 'Opening soon';
    action = 'picks';
    ctaVariant = 'disabled';
    badge = undefined;
  } else if (racing && !hasEntry) {
    title = 'Calls closed';
    body = 'You missed the window for this stage.';
    ctaLabel = 'See standings';
    action = 'board';
    ctaVariant = 'dark';
    badge = <HubLiveBadge />;
  } else if (!hasEntry && editable) {
    title = 'Call your duels';
    body = 'Pick who finishes ahead in each of six duels, then rank them by confidence.';
    ctaLabel = 'Call my duels';
    action = 'picks';
    ctaNote = 'Six terrain-matched duels · rank by confidence when all are in';
    badge = undefined;
  } else if (hasEntry && editable && !allCalled) {
    title = `${TOTAL - calledCount} duels to call`;
    body = 'Finish calling every duel — ranking unlocks once all six are in.';
    ctaLabel = 'Continue my calls';
    action = 'picks';
    badge = undefined;
  } else if (racing) {
    title = settledCount >= TOTAL ? 'Final results landing' : 'Duels locked';
    body = 'Your calls are locked in for this stage.';
    ctaLabel = 'Division board';
    action = 'board';
    ctaVariant = 'dark';
    badge = <HubLiveBadge />;
  } else {
    title = 'Call your duels';
    body = 'Pick who finishes ahead in each of six duels, then rank them by confidence.';
    ctaLabel = 'Call my duels';
    action = 'picks';
    badge = undefined;
  }

  return (
    <CyclingDuelsHubSection
      pager={pager}
      title={title}
      body={body}
      lockAt={editable && lockAt > now ? lockAt : null}
      now={now}
      badge={badge}
      cta={{ label: ctaLabel, onClick: () => onPredict(action), variant: ctaVariant }}
      ctaNote={ctaNote}
    >
      {showPlaceholderLadder ? (
        <StakeLadderPlaceholder embedded calledCount={calledCount} />
      ) : null}
    </CyclingDuelsHubSection>
  );
}
